import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ring-offset-background",
  {
    variants: {
      variant: {
        default: "bg-brand-primary text-white hover:bg-brand-primary-dark",
        secondary: "bg-brand-secondary text-white hover:bg-brand-secondary-dark",
        outline:
          "border border-surface-border-light bg-transparent text-foreground-light hover:border-brand-secondary/40 hover:bg-brand-secondary/5 hover:text-brand-secondary dark:border-surface-border-dark dark:text-foreground-dark dark:hover:border-brand-secondary/50 dark:hover:bg-brand-secondary/10 dark:hover:text-brand-secondary-light",
        ghost:
          "bg-transparent text-foreground-muted-light hover:bg-brand-secondary/10 hover:text-brand-secondary active:bg-brand-secondary/15 dark:text-foreground-muted-dark dark:hover:bg-brand-secondary/20 dark:hover:text-brand-secondary-light active:scale-95",
        destructive: "bg-state-validation text-white hover:opacity-90",
        link: "underline-offset-4 hover:underline text-brand-secondary",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;

export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>["size"]>;

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "size"> {
  asChild?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  return <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />;
});
Button.displayName = "Button";

export { buttonVariants };
